import dayjs from "dayjs";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);
dayjs.locale("es");

export function formatToYMD(date: Date): string {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(date)
    .split(" ")
    .join("");
}

export function formatToRelativeTime(date: string): string {
  return dayjs(date).fromNow();
}

export function parseLocalDateFromString(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatDateToCommaString(dateStr: string): string {
  return dayjs(dateStr).format("D [de] MMMM, YYYY");
}

export function formatDateTime(dateStr: string): string {
  return dayjs(dateStr).format("DD MMM, YYYY | HH:mm A");
}

export function getFormattedElapsedDays(creationDate: string): string {
  const timeDifference = Date.now() - new Date(creationDate).getTime();
  let formattedDiff = "";
  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) formattedDiff += `${days}d `;
  if (hours > 0) formattedDiff += `${hours}h `;
  formattedDiff += `${minutes}m`;

  return formattedDiff;
}

export function getFormattedElapsedDaysWithColor(creationDate: string): {
  formatted: string;
  colorClass: string;
} {
  const timeDifference = Date.now() - new Date(creationDate).getTime();
  let formattedDiff = "";
  const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) formattedDiff += `${days}d `;
  if (hours > 0) formattedDiff += `${hours}h `;
  formattedDiff += `${minutes}m`;

  // Determine color class based on days
  let colorClass = "text-muted";
  if (days <= 5) {
    colorClass = "text-success";
  } else if (days >= 6 && days <= 10) {
    colorClass = "text-warning";
  } else if (days > 10) {
    colorClass = "text-danger";
  }

  return { formatted: formattedDiff, colorClass };
}

export const getDateFromString = (dateStr: string) => {
  return dateStr && dayjs(dateStr, "DD/MM/YYYY", true).isValid()
    ? dayjs(dateStr, "DD/MM/YYYY").toDate()
    : undefined;
};

/**
 * Converts a Date to ISO string format with local timezone (no UTC conversion)
 * @param date - The date to convert
 * @returns ISO string in format YYYY-MM-DDTHH:mm:ss or undefined if date is invalid
 */
export function localDateToIso(date?: Date): string | undefined {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}
